import type { Quotation, QuotationFormItem } from "./types";

export function formatMoney(amountMinorUnits: unknown, currency = "INR"): string {
  const amount = Number(amountMinorUnits || 0) / 100;
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency || "INR"} ${amount.toFixed(2)}`;
  }
}

export function quotationStatusLabel(status: unknown): string {
  return String(status || "Draft")
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function quotationDisplayStatus(
  quotation: Pick<Quotation, "status" | "superseded_at" | "superseded_by_quotation_id">,
): string {
  return quotation.superseded_at || quotation.superseded_by_quotation_id
    ? "SUPERSEDED"
    : quotation.status;
}

export function quotationRevisionLabel(quotation: Pick<Quotation, "quotation_number" | "revision_number">): string {
  return `${quotation.quotation_number} · Revision ${quotation.revision_number}`;
}

export function quantityFromMilliunits(value: unknown): string {
  const milliunits = Math.max(0, Number(value || 0));
  const whole = Math.floor(milliunits / 1000);
  const fraction = milliunits % 1000;
  return fraction ? `${whole}.${String(fraction).padStart(3, "0")}`.replace(/0+$/, "") : String(whole);
}

export function majorAmountFromMinorUnits(value: unknown): string {
  const amount = Number(value || 0) / 100;
  return amount.toFixed(2).replace(/\.00$/, "");
}

export function minorUnitsFromMajorAmount(value: string): number {
  const normalized = value.trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) return Number.NaN;
  return Math.round(Number(normalized) * 100);
}

export function quotationTotals(
  items: QuotationFormItem[],
  discountMinorUnits: number,
  taxRateBasisPoints: number,
  gstSupplyType?: "intra_state" | "inter_state" | null,
): {
  subtotal: number;
  taxable: number;
  tax: number;
  cgst: number;
  sgst: number;
  igst: number;
  total: number;
} {
  const subtotal = items.reduce((sum, item) => {
    const quantityMilliunits = Math.round(Number(item.quantity || 0) * 1000);
    const unitAmount = minorUnitsFromMajorAmount(item.unit_amount);
    if (!Number.isFinite(quantityMilliunits) || !Number.isFinite(unitAmount)) return sum;
    return sum + Math.floor((quantityMilliunits * unitAmount + 500) / 1000);
  }, 0);
  const taxable = Math.max(0, subtotal - Math.max(0, discountMinorUnits));
  const rate = Math.max(0, taxRateBasisPoints);
  let cgst = 0;
  let sgst = 0;
  let igst = 0;
  let tax = 0;
  if (gstSupplyType === "intra_state") {
    const componentRate = rate / 2;
    cgst = Math.floor((taxable * componentRate + 5000) / 10000);
    sgst = cgst;
    tax = cgst + sgst;
  } else {
    tax = Math.floor((taxable * rate + 5000) / 10000);
    if (gstSupplyType === "inter_state") igst = tax;
  }
  return {
    subtotal,
    taxable,
    tax,
    cgst,
    sgst,
    igst,
    total: taxable + tax,
  };
}

export function quotationFileName(quotation: Pick<Quotation, "quotation_number" | "revision_number">): string {
  const number = quotation.quotation_number.replace(/[^A-Za-z0-9._-]+/g, "-");
  return `Quotation-${number}-R${quotation.revision_number}.pdf`;
}
